import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import styles from "./WaysToStrengthenProfile.module.css";
import SkorProtectConfirmModal from "./SkorProtectConfirmModal";

const Row = ({
  title,
  description,
  actionLabel,
  onAction,
  disabled,
  processing = false,
  tooltipId,
}) => {
  const { _t } = useTranslator();
  const tooltipText = processing
    ? _t(
        "profileProcessingWait",
        "Your profile is still being processed. Please wait until it's ready."
      )
    : disabled
      ? _t(
          "profileOutdatedRenewFirst",
          "Your profile is outdated. Please renew your profile first."
        )
      : undefined;

  return (
    <div className={styles.row}>
      <div className={styles.text}>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.desc}>{description}</p>
      </div>
      {disabled ? (
        <div
          style={{ display: "inline-block", flexShrink: 0 }}
          data-tooltip-id={tooltipId}
        >
          <button
            type="button"
            className={styles.btn}
            onClick={onAction}
            disabled={disabled}
            style={{ opacity: 0.5, cursor: "not-allowed" }}
          >
            {actionLabel}
          </button>
          <ReactTooltip
            id={tooltipId}
            place="top"
            className="react-tooltip-styling"
            render={() => <p>{tooltipText}</p>}
          />
        </div>
      ) : (
        <button type="button" className={styles.btn} onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};

const WaysToStrengthenProfile = ({
  missingDocsCount = 0,
  isBankConnected = false,
  hasSkorProtect = false,
  onUpdateDocs,
  onLinkBank,
  onBuyProtect,
  onSkorProtectEnabled,
  disabled = false,
  processing = false,
}) => {
  const { _t } = useTranslator();
  const [showSkorModal, setShowSkorModal] = useState(false);
  const showDocs = missingDocsCount > 0;
  const showBank = !isBankConnected;
  const showProtect = !hasSkorProtect;

  if (!showDocs && !showBank && !showProtect) return null;

  const handleSkorClick = () => {
    if (disabled) return;
    if (onBuyProtect) onBuyProtect();
    setShowSkorModal(true);
  };

  return (
    <section
      className={`tenant-profile-placeholder-card tenant-profile-placeholder-card--strengthen ${styles.card}`}
      aria-labelledby="ways-to-strengthen-profile-title"
    >
      <h2 id="ways-to-strengthen-profile-title" className={styles.heading}>
        {_t("waysToStrengthenProfile", "Ways to strengthen your profile")}
      </h2>
      <p className={styles.subtitle}>
        {_t(
          "waysToStrengthenProfileSubtitle",
          "A few steps that build on each other, roughly in order."
        )}
      </p>

      <div className={styles.list}>
        {showDocs && (
          <Row
            title={_t("updateYourDocuments", "Update your documents")}
            description={_t(
              "updateYourDocumentsDesc",
              "Refresh anything older than a few months so your profile reflects your current situation"
            )}
            actionLabel={_t("update", "Update")}
            onAction={onUpdateDocs}
            disabled={disabled}
            processing={processing}
            tooltipId="wsp-update-docs-tooltip"
          />
        )}

        {showBank && (
          <Row
            title={_t("linkABankAccount", "Link a bank account")}
            description={_t(
              "linkABankAccountDesc",
              "Unlocks your Bank behaviour score and raises your completeness"
            )}
            actionLabel={_t("link", "Link")}
            onAction={onLinkBank}
            disabled={disabled}
            processing={processing}
            tooltipId="wsp-link-bank-tooltip"
          />
        )}

        {showProtect && (
          <Row
            title={_t("addSkorProtect", "Add Skor Protect")}
            description={_t(
              "addSkorProtectDesc",
              "Extra backup coverage, especially while your rental fit is elevated"
            )}
            actionLabel={_t("add", "Add")}
            onAction={handleSkorClick}
            disabled={disabled}
            processing={processing}
            tooltipId="wsp-skor-protect-tooltip"
          />
        )}
      </div>
      <SkorProtectConfirmModal
        isOpen={showSkorModal}
        onClose={() => setShowSkorModal(false)}
        onConfirmed={() => onSkorProtectEnabled?.()}
      />
    </section>
  );
};

export default WaysToStrengthenProfile;
